/**
 * Feature Flag Registry.
 *
 * Resolution pipeline at GET /api/v1/feature-flags (in edge-api):
 *
 *   1. Platform defaults <- `DEFAULT_FLAGS` (derived from Product Feature Catalog)
 *       ↓ override by
 *   2. Tier defaults    ← `TIER_FEATURE_DEFAULTS` in @aica/stripe
 *       ↓ override by
 *   3. Org overrides    ← rows in D1 `feature_flags` table
 *       ↓ forced-false by
 *   4. Unimplemented guard ← `UNIMPLEMENTED_FLAG_KEYS` (from `isImplemented: false`)
 *
 * Step 4 is the safety net: if a feature is marked `isImplemented: false`
 * in the Product Feature Catalog, we refuse to let the flag resolve to `true`
 * no matter what layer
 * tried to turn it on. That prevents an admin from toggling a dead
 * feature and confusing themselves (or worse, users) into thinking
 * something is available when the backing code doesn't exist yet.
 *
 * The admin UI imports the same exported registry to render its management
 * surface, so the frontend "Default" column and the backend resolver cannot drift.
 */

import type { TierFeatureRefusal } from '@aica/api-types';
import {
  PRODUCT_FEATURE_CATALOG,
  PRODUCT_FEATURE_FLAG_SPECS,
  PRODUCT_PACKAGE_KEYS,
  getFeatureFlagDefaultsForPackage,
  hasFeatureFlag,
  isFeatureIncludedByDefault,
  type ProductPackageKey,
  type ProductFeatureFlagCategory,
  type ProductFeatureFlagKey,
  type ProductFeatureFlagSpecWithStatus,
  type ProductFeatureStatus,
} from './product-features';

export type FeatureFlagCategory = ProductFeatureFlagCategory;
export type FeatureFlagKey = ProductFeatureFlagKey;
export type FeatureFlagSpec = ProductFeatureFlagSpecWithStatus;
export type FeatureStatus = ProductFeatureStatus;

export const FEATURE_FLAG_SPECS: readonly FeatureFlagSpec[] = PRODUCT_FEATURE_FLAG_SPECS;

/**
 * Canonical key for the stage-aware scoring flag (Phase B/C). Shared by the
 * stage-capture pipeline step and the outcome-sync cron so the two can't drift
 * on the string literal. Typed as `FeatureFlagKey` so a catalog rename surfaces
 * here as a compile error rather than a silent per-file miss.
 */
export const STAGE_AWARE_SCORING_FLAG_KEY: FeatureFlagKey = 'stageAwareScoring';

/**
 * Signup-boost experiment (offer layer, Phase 6). Typed against FeatureFlagKey
 * for the same reason as above: renaming the catalog entry breaks the build
 * instead of silently disabling the experiment at every call site.
 */
export const SIGNUP_BOOST_FLAG_KEY: FeatureFlagKey = 'signupMinuteBoost';

/** Aelo tab in Uspacy deal and lead cards (#746): per-organization, off by default. */
export const USPACY_CARD_TAB_FLAG_KEY: FeatureFlagKey = 'embeddedUspacy';

/** Pro trial for free organizations (#2816): starts NEW trials only. */
export const PRO_TRIAL_FLAG_KEY: FeatureFlagKey = 'enableProTrial';

/**
 * Org-hierarchy supervision kill-switch (Task 13 of the org-hierarchy access
 * layer). Typed against FeatureFlagKey for the same reason as above: a
 * catalog rename breaks the build at every call site instead of silently
 * reading as an unknown key everywhere the flag is checked.
 */
export const ORG_HIERARCHY_SUPERVISION_FLAG_KEY: FeatureFlagKey =
  'orgHierarchySupervision';

/**
 * AI assistant gate (docs/plans/2026-09-11-assistant.md §7.2). Read by the
 * edge-api tier + flag gate and by the UI entry points; typed against
 * FeatureFlagKey for the same reason as above.
 */
export const ASSISTANT_FLAG_KEY: FeatureFlagKey = 'enableAIAssistant';

/**
 * «Голос клиента» (#848), Pro и выше. Read by the edge-api tier + flag gate of
 * `/analytics/voice`, by the monthly email and by the UI entry points.
 */
export const VOICE_OF_CUSTOMER_FLAG_KEY: FeatureFlagKey = 'enableVoiceOfCustomer';

/**
 * Вопросы клиентов и причины отказа (#843, #844) — блоки внутри «Голоса
 * клиента», Pro и выше. Read by the edge-api gate of `/analytics/voice` (the
 * optional `customerQuestions` key) and of the FAQ draft, by the workflows-api
 * gate (FAQ draft, lost-deal reason writers, the monthly email's questions and
 * declines lines) and by the UI entry points. Typed against FeatureFlagKey for
 * the same reason as above.
 */
export const CUSTOMER_QUESTIONS_FLAG_KEY: FeatureFlagKey = 'enableCustomerQuestions';

/**
 * Набор тем организации (#845) — a released beta: on by the tier default from
 * Pro up (the catalog's `packages`), and the organization can switch it itself.
 * An organization connected to Uspacy also needs processing instruction 1.10
 * confirmed (workflows-api `uspacy-themes-gate.ts`). Read by every `/analytics/voice/themes` route, after the Voice of Customer
 * gate, and by the UI's Themes panel (web flag map, embedded session subset).
 * Typed against FeatureFlagKey for the same reason as above.
 */
export const CONVERSATION_THEMES_FLAG_KEY: FeatureFlagKey = 'enableConversationThemes';

/**
 * CRM delivery rules (#814). Read by the routing step that evaluates the rules
 * and by the edge-api routes that create and modify them — the flag used to
 * hide only the settings section, so `/api/v1/delivery-rules` stayed an open
 * door to a surface the catalog calls unavailable: a rule created through it
 * carried a non-empty `fieldsToUpdate` and the routing step loaded it without
 * asking. Typed against FeatureFlagKey for the same reason as above.
 */
export const DELIVERY_RULES_FLAG_KEY: FeatureFlagKey = 'enableDeliveryRules';

/**
 * Custom call criteria (#857). Read by every `/api/v1/call-criteria` route —
 * reads included, unlike delivery rules: an organization without the flag has
 * no criteria to read. Typed against FeatureFlagKey for the same reason as above.
 */
export const CALL_CRITERIA_FLAG_KEY: FeatureFlagKey = 'enableCallCriteria';

/**
 * Built-in call criteria visible to the customer (#868). Off in stage E1: every
 * customer route reads criteria through a customer-mode repository that hides
 * built-ins and, through their ids, their answers. Typed against
 * FeatureFlagKey for the same reason as above.
 */
export const BUILTIN_CRITERIA_VISIBLE_FLAG_KEY: FeatureFlagKey = 'builtinCriteriaVisible';

/**
 * Stage E2 of built-in criteria for an org — the EFFECTIVE value of
 * `builtinCriteriaVisible` (#2490, plan `docs/plans/2026-10-18-builtin-criteria-e2.md`,
 * decisions 1 and 12): the flag as resolved, AND custom criteria on (the
 * built-ins live in the same routes), AND no own LLM key — a BYOK org gets no
 * shadow evaluation, so its built-ins would show without answers — AND the org
 * already has built-in rows (`seeded`): without them there is nothing to show,
 * and, until stage E3 (#2846) removed the profile toggles, they would have
 * been locked by a criterion that does not exist. One definition for every place the flag is read: the edge-api flag resolver
 * (and through it the UI flag map and `customerCriteriaAccess`) and
 * workflows-api `isOrgFlagEnabled` (and through it `loadCriterionCatalog`).
 */
export function builtinCriteriaEffective(input: {
  builtinCriteriaVisible: boolean;
  callCriteria: boolean;
  ownLlmKey: boolean;
  seeded: boolean;
}): boolean {
  return (
    input.builtinCriteriaVisible && input.callCriteria && !input.ownLlmKey && input.seeded
  );
}

/**
 * Flags only the platform owner may write (`platformOwnerOnly` in the catalog).
 * Enforced by PUT/DELETE /api/v1/feature-flags/:key for the caller's own org
 * too: `manage_settings` lets a customer flip their own flags, and a gate like
 * `builtinCriteriaVisible` exists precisely to keep something from them.
 * Reading such a flag is unrestricted.
 */
export const PLATFORM_OWNER_ONLY_FLAG_KEYS: ReadonlySet<string> = new Set(
  FEATURE_FLAG_SPECS.filter((spec) => spec.platformOwnerOnly === true).map(
    (spec) => spec.key
  )
);

/**
 * The unified dashboard's quality block, closed preview (#1235). Gates
 * `/api/v1/dashboard/quality` and the dashboard through the flag map — on the
 * web from `/feature-flags`, in embedded Bitrix24 from the session's `flags`
 * subset (#1577). Typed against FeatureFlagKey for the same reason as above.
 */
export const DASHBOARD_QUALITY_SLICE_FLAG_KEY: FeatureFlagKey =
  'enableDashboardQualitySlice';

/**
 * The unified dashboard's shell, closed preview (#1337). Platform-owner-only:
 * on, `/dashboard` (and the embedded Overview, from the session's `flags`
 * subset) renders the six-tab shell for everyone but agents and the platform
 * owner; an agent gets the shell's personal view, the tab «mine» (#1338 PR C),
 * and the platform owner keeps its dashboard. Typed against FeatureFlagKey for the same reason as above.
 */
export const UNIFIED_DASHBOARD_FLAG_KEY: FeatureFlagKey = 'enableUnifiedDashboard';

/**
 * The unified dashboard's «Продажи» tab (#1339), Pro and up (owner 12.10).
 * Read by the tier + flag gate of `GET /api/v1/dashboard/sales`, the embedded
 * session's `gates` and the tab. Typed against FeatureFlagKey for the same
 * reason as above.
 */
export const DASHBOARD_SALES_FLAG_KEY: FeatureFlagKey = 'enableDashboardSales';

/**
 * Deal State Summary (#766), Pro and up. Read by the sales-methodology routes
 * (`/api/v1/crm/…/sales-methodology`) and the «Методика продаж» block of the
 * Deal stages tab, the deal routes and the deal-state step. Pro and up, the
 * plan first and then the flag (`resolveTierFeatureAccess`). Typed against
 * FeatureFlagKey for the same reason as above.
 */
export const DEAL_STATE_SUMMARY_FLAG_KEY: FeatureFlagKey = 'enableDealStateSummary';

/**
 * Narrow analytics access grants (E1.5, #1336), Pro and up: the tier first,
 * then the flag, like deal state. What it gates and why — the catalog note of
 * `access.analytics_grants`. Typed against FeatureFlagKey for the same reason
 * as above.
 */
export const ACCESS_GRANTS_FLAG_KEY: FeatureFlagKey = 'enableAccessGrants';

/**
 * Map of key → platform-level default. Generated from the registry so
 * the backend resolver and the admin UI agree on what the baseline is.
 */
export const DEFAULT_FLAGS: Readonly<Record<FeatureFlagKey, boolean>> = Object.freeze(
  Object.fromEntries(
    FEATURE_FLAG_SPECS.map((spec) => [spec.key, spec.defaultValue])
  ) as Record<FeatureFlagKey, boolean>
);

/**
 * Keys that are not wired to real behavior. Backend resolution forces
 * these to `false` after tier + org overrides are applied.
 */
export const UNIMPLEMENTED_FLAG_KEYS: ReadonlySet<FeatureFlagKey> =
  new Set<FeatureFlagKey>(
    FEATURE_FLAG_SPECS.filter((spec) => !spec.isImplemented).map(
      (spec) => spec.key as FeatureFlagKey
    )
  );

/**
 * Apply the safety net: force any unimplemented flag to `false`.
 * Mutates a copy of the input and returns it. No per-resolution
 * exceptions: the assistant's preview list that once needed one went with
 * its dark period (#765 PR 3b).
 */
export function enforceUnimplemented(
  flags: Record<string, boolean>
): Record<string, boolean> {
  const resolved = { ...flags };
  for (const key of UNIMPLEMENTED_FLAG_KEYS) {
    if (key in resolved) resolved[key] = false;
  }
  return resolved;
}

const isPackageKey = (tier: string): tier is ProductPackageKey =>
  (PRODUCT_PACKAGE_KEYS as readonly string[]).includes(tier);

/** Tier defaults per package, derived once — the catalog does not change at run time. */
let tierDefaultsCache: Record<ProductPackageKey, Record<string, boolean>> | undefined;
const tierDefaults = (tier: ProductPackageKey): Record<string, boolean> => {
  tierDefaultsCache ??= Object.fromEntries(
    PRODUCT_PACKAGE_KEYS.map((key) => [key, getFeatureFlagDefaultsForPackage(key)])
  ) as Record<ProductPackageKey, Record<string, boolean>>;
  return tierDefaultsCache[tier];
};

/**
 * One flag through the same ladder as edge-api's `resolveOrgFlags`: platform
 * default → the tier default derived from the catalog's packages → the org's
 * override row → the unimplemented guard.
 *
 * For an app that cannot reach `@aica/stripe` (workflows-api): the tier
 * defaults there are this same catalog derivation, so the two ends answer
 * alike without the package graph turning over. Reading the override row
 * alone answers "off" for an org with no row even when its tier includes the
 * feature — the trap a feature walks into the moment it enters a package.
 *
 * The tier edge cases follow `resolveOrgFlags` exactly: a missing or empty
 * tier reads as `free`; a tier the catalog does not know contributes no tier
 * layer at all (`getTierFeatureDefaults` returns an empty map for it), so the
 * platform default stands.
 */
export function resolveFeatureFlag(
  flagKey: FeatureFlagKey,
  tier: string | null | undefined,
  override: boolean | null
): boolean {
  if (UNIMPLEMENTED_FLAG_KEYS.has(flagKey)) return false;
  if (override !== null) return override;
  const effectiveTier = tier || 'free';
  const tierDefault = isPackageKey(effectiveTier)
    ? tierDefaults(effectiveTier)[flagKey]
    : undefined;
  return tierDefault ?? DEFAULT_FLAGS[flagKey];
}

/**
 * A feature sold from a tier up: its flag, and the lowest package that
 * includes it.
 */
export interface TierFeatureGate {
  readonly flagKey: FeatureFlagKey;
  readonly minTier: ProductPackageKey;
}

/**
 * Every catalog feature with a flag whose packages include it on some tiers
 * and not on others (#1231). For these a bare "off" means two different
 * things — the plan is too low (upgrade) or an administrator switched it off
 * — so `GET /feature-flags` answers them with a refusal reason, not a boolean.
 *
 * A feature off on every tier (a closed preview, or a flag the resolver forces
 * off: `isImplemented: false`) or on on every tier is not here: its flag alone
 * decides, and "off" can only mean the switch.
 * `minTier` is the lowest package in `PRODUCT_PACKAGE_KEYS` order that
 * includes the feature; the catalog test pins that every package above it
 * includes it too, which is what makes a single floor a faithful reading.
 */
export const TIER_FEATURE_GATES: readonly TierFeatureGate[] = Object.freeze(
  PRODUCT_FEATURE_CATALOG.flatMap((feature) => {
    // A flag the resolver forces off (`isImplemented: false`) is off on every
    // tier whatever the package matrix says — "off" can only mean "closed".
    if (!hasFeatureFlag(feature) || !feature.flag.isImplemented) return [];
    const included = PRODUCT_PACKAGE_KEYS.filter((tier) =>
      isFeatureIncludedByDefault(feature.packages[tier])
    );
    const [minTier] = included;
    if (!minTier || included.length === PRODUCT_PACKAGE_KEYS.length) return [];
    return [{ flagKey: feature.flag.key as FeatureFlagKey, minTier }];
  })
);

/**
 * The gate of a feature sold from a tier up, by its flag. Throws for a flag
 * that is not in `TIER_FEATURE_GATES`: a caller asking for the floor of a
 * feature that has none has a wrong key, and a silent default floor would
 * leave that door open or shut by accident. The routes, the embedded session
 * and the monthly email take their `minTier` from here instead of writing
 * `'pro'` next to the flag key — so a change of the floor in the catalog
 * moves the route and the flag map's `gates` together (#1231).
 */
export function getTierFeatureGate(flagKey: FeatureFlagKey): TierFeatureGate {
  const gate = findTierFeatureGate(flagKey);
  if (!gate) throw new Error(`Feature flag ${flagKey} is not sold from a tier up`);
  return gate;
}

/**
 * The gate of a feature sold from a tier up, or `undefined` for a flag that
 * has none (on, or off, on every tier — its flag alone decides). A feature the
 * catalog switches off (`isImplemented: false`, the kill switch) drops out of
 * `TIER_FEATURE_GATES` too, and its flag is forced off. For a caller that must
 * keep working while the catalog changes the feature's packages or switches
 * it off: a route that took `getTierFeatureGate` would answer 500 the day the
 * floor is lifted, and a gate that reads `undefined` as "closed" keeps the
 * rest of its screen working.
 */
export function findTierFeatureGate(
  flagKey: FeatureFlagKey
): TierFeatureGate | undefined {
  return TIER_FEATURE_GATES.find((candidate) => candidate.flagKey === flagKey);
}

export type TierFeatureAccess =
  { allowed: true } | { allowed: false; code: TierFeatureRefusal };

/**
 * The rule for who may use a feature sold from a tier up, on values already
 * read: the org's tier, the feature's floor and the flag as resolved for the
 * org (the full ladder — an override row alone is not the flag).
 *
 *   1. tier — below `minTier` → `tier_not_eligible` (the message for a lower
 *      plan is "upgrade", not "your administrator switched something off",
 *      which is what the flag would say next: it is off for lower tiers only
 *      as a consequence of the tier default);
 *   2. flag → `feature_disabled`.
 *
 * A missing tier (no org row, `null`) reads as `free`; a tier the catalog does
 * not know ranks below every floor, so a row we cannot make sense of refuses
 * rather than admits. One function for edge-api (routes, flag map `gates`) and
 * workflows-api (the monthly email): two copies are how they once disagreed.
 */
export function decideTierFeatureAccess(
  tier: string | null | undefined,
  minTier: ProductPackageKey,
  flagEnabled: boolean
): TierFeatureAccess {
  const rank = (PRODUCT_PACKAGE_KEYS as readonly string[]).indexOf(tier ?? 'free');
  if (rank < PRODUCT_PACKAGE_KEYS.indexOf(minTier)) {
    return { allowed: false, code: 'tier_not_eligible' };
  }
  return flagEnabled ? { allowed: true } : { allowed: false, code: 'feature_disabled' };
}
